import { GrainUsageTypeEnum } from '@/common/constants';
import { StorageTypeEnum } from '@/common/constants/parties.constants';
import { cleanObject } from '@/lib/clean-object';
import useStore from '@/lib/store';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useRef } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { IParty } from '../models';
import { PartiesSchema } from '../schemas';
import usePartyGrainStore from '../store/usePartyGrainStore';

export const usePartiesGrain = () => {
  const navigate = useNavigate();
  const { user } = useStore();
  const {
    addPartyGrain,
    fetchPartyGrain,
    updatePartyGrain,
    fetchPlantsGrain,
    fetchCropTypesByPlant,
    fetchCropGenerationsByPlantId,
  } = usePartyGrainStore();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isFetchingSingle = useRef(false);

  const form = useForm({
    resolver: yupResolver(PartiesSchema),
    context: { slug: id, grainUsageType: searchParams.get('activeTab') },
    defaultValues: {
      storage_type: StorageTypeEnum.SEED,
    },
  });
  const activeTab = searchParams.get('activeTab');

  const plantId = useWatch({
    name: 'plant_id',
    control: form.control,
  });

  useEffect(() => {
    fetchPlantsGrain();
  }, []);

  useEffect(() => {
    if (isFetchingSingle.current) return;

    if (plantId) {
      fetchCropTypesByPlant(String(plantId));
      if (activeTab === GrainUsageTypeEnum.SEED) {
        fetchCropGenerationsByPlantId(String(plantId));
      }
    }
  }, [plantId]);

  const fetchSingle = async () => {
    if (id) {
      isFetchingSingle.current = true;

      const data: IParty = await fetchPartyGrain(id!);

      form.reset({
        plant_id: data?.plant_id,
        place_number: data?.place_number,
        party_number: data?.party_number,
        crop_type_id: data?.crop_type_id,
        class: data?.class,
        capacity: data?.capacity,
      });

      if (activeTab === GrainUsageTypeEnum.GOODS) {
        form.setValue('storage_type', data?.storage_type);
        form.setValue('place_name', data?.place_name);
      }

      if (data?.plant_id) {
        await fetchCropTypesByPlant(String(data?.plant_id));

        if (activeTab === GrainUsageTypeEnum.SEED && data?.crop_generation_id) {
          await fetchCropGenerationsByPlantId(String(data.plant_id));
          form.setValue('crop_generation_id', data.crop_generation_id);
        }
      }

      isFetchingSingle.current = false;
    }
  };

  useEffect(() => {
    if (id) {
      fetchSingle();
    }
  }, [id]);

  const onSubmit: SubmitHandler<any> = async (data) => {
    const { crop_type_id, crop_generation_id, plant_id, ...rest } = data;

    const basePayload = {
      ...rest,
      crop_type_id: Number(crop_type_id),
      crop_generation_id: crop_generation_id
        ? Number(crop_generation_id)
        : null,
      plant_id: Number(plant_id),
      usage_type: activeTab,
      cluster_id: user?.cluster_id,
    };

    if (id) {
      await updatePartyGrain(id, cleanObject(basePayload));
    } else {
      await addPartyGrain(cleanObject(basePayload) as IParty);
    }
    form.reset();
    navigate(-1);
  };

  const handleForm = form.handleSubmit(onSubmit);

  return {
    slug: id,
    form,
    onSubmit: handleForm,
  };
};
